import type {
  InventoryPage,
  InventoryReadItem,
  WarehouseList,
} from '../api/active/generated/inventory';
import type {
  InventoryReadStatus,
  InventoryWarehouseCode,
} from './inventoryApi';

export const INVENTORY_WAREHOUSE_CODES = [
  '0053',
  '013',
  '0075',
  '0064',
  '0063',
  '0065',
] as const satisfies readonly InventoryWarehouseCode[];

export type InventoryWarehouseView = {
  warehouseId: string;
  warehouseCode: InventoryWarehouseCode;
  name: string;
};

export type InventoryQuantityView = {
  warehouseCode: InventoryWarehouseCode;
  displayQuantity: number | null;
  observedAt: string | null;
  readStatus: InventoryReadStatus;
};

export type InventoryDeviceView = {
  /** Opaque server attestation; held only in the authorization-scoped runtime. */
  displayReceipt?: string;
  deviceId: string;
  deviceRevisionId: string;
  deviceName?: string;
  deviceSku?: string;
  totalDisplayQuantity: number | null;
  observedAt: string | null;
  readStatus: InventoryReadStatus;
  warehouses: InventoryQuantityView[];
};

export type InventoryPageView = {
  items: InventoryDeviceView[];
  meta: InventoryPage['meta'];
};

export type InventorySelectionView = {
  displayQuantity: number | null;
  observedAt: string | null;
  readStatus: InventoryReadStatus;
};

function assertWarehouseCodes(codes: readonly string[]) {
  if (
    codes.length !== INVENTORY_WAREHOUSE_CODES.length ||
    codes.some((code, index) => code !== INVENTORY_WAREHOUSE_CODES[index])
  ) {
    throw new Error('库存服务返回的六仓目录与已批准合同不一致。');
  }
}

function approvedWarehouseCode(index: number): InventoryWarehouseCode {
  const code = INVENTORY_WAREHOUSE_CODES[index];
  if (!code) throw new Error('库存服务返回了超出六仓合同的目录项。');
  return code;
}

export function mapWarehouseList(value: WarehouseList) {
  assertWarehouseCodes(value.items.map((item) => item.warehouseCode));
  const warehouseIds = value.items.map((item) => item.warehouseId);
  if (
    warehouseIds.some((warehouseId) => !/^[1-9][0-9]*$/.test(warehouseId)) ||
    new Set(warehouseIds).size !== warehouseIds.length
  ) {
    throw new Error('库存服务返回的六仓内部身份与已批准合同不一致。');
  }
  return value.items.map<InventoryWarehouseView>((item, index) => ({
    warehouseId: item.warehouseId,
    warehouseCode: approvedWarehouseCode(index),
    name: item.name,
  }));
}

export function mapInventoryItem(
  value: InventoryReadItem,
): InventoryDeviceView {
  assertWarehouseCodes(value.warehouses.map((item) => item.warehouseCode));
  return {
    ...(value.displayReceipt ? { displayReceipt: value.displayReceipt } : {}),
    deviceId: value.deviceId,
    deviceRevisionId: value.deviceRevisionId,
    deviceName: value.deviceName,
    deviceSku: value.deviceSku,
    totalDisplayQuantity: value.totalDisplayQuantity,
    observedAt: value.observedAt,
    readStatus: value.readStatus,
    warehouses: value.warehouses.map((item, index) => ({
      warehouseCode: approvedWarehouseCode(index),
      displayQuantity: item.displayQuantity,
      observedAt: item.observedAt,
      readStatus: item.readStatus,
    })),
  };
}

export function mapInventoryPage(value: InventoryPage): InventoryPageView {
  return { items: value.items.map(mapInventoryItem), meta: value.meta };
}

export function selectInventoryView(
  item: InventoryDeviceView,
  warehouseCode?: InventoryWarehouseCode,
): InventorySelectionView {
  if (!warehouseCode) {
    return {
      displayQuantity: item.totalDisplayQuantity,
      observedAt: item.observedAt,
      readStatus: item.readStatus,
    };
  }
  const warehouse = item.warehouses.find(
    (candidate) => candidate.warehouseCode === warehouseCode,
  );
  if (!warehouse) {
    return { displayQuantity: null, observedAt: null, readStatus: 'UNKNOWN' };
  }
  return warehouse;
}
